import {
  Alert,
  CircularProgress,
  List,
  ListItem,
  Typography,
} from "@mui/material";
import PrimaryButton from "../../components/custom/PrimaryButton";
import { Box } from "@mui/system";
import { Form, Formik } from "formik";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components/macro";
import TextField from "../../components/custom/TextField";
import InfoIcon from "@mui/icons-material/Info";
import Tooltip, { tooltipClasses } from "@mui/material/Tooltip";
import { Check, Clear } from "@mui/icons-material";
import * as Yup from "yup";
import { useMutation } from "react-query";
import { resetPassword } from "../../services/resetPasswordService";
import { useNavigate, useSearchParams } from "react-router-dom";

const BodyWrapper = styled(Box)`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
  flex-direction: column;
  padding: 3rem;
  /* margin-top: 5rem; */
  /* background-color: red; */
`;
const TypographyWrapper = styled(Box)`
  display: flex;
  justify-content: center;
  flex-direction: column;
  width: 100%;
  height: 10%;
`;

const StyledForm = styled(Form)`
  width: 100%;
  height: 70%;
`;

const LabelWrapper = styled(Box)`
  display: flex;
  align-items: center;
`;

const HtmlTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: "#f5f5f9",
    color: "rgba(0, 0, 0, 0.87)",
    width: 300,
    // height: 140,
    height: (props) => 100 * props.ratio,
    border: "1px solid #dadde9",
    borderRadius: "1rem",
    margin: 0,
    display: "flex",
    flexDirection: "column",
  },
}));

const ListWrapper = styled(List)`
  height: 20%;
  padding: 0;
  li {
    padding: 0;
  }
  margin-left: 1rem;
`;
const ListItemsWrapper = styled(Box)`
  display: flex;
  align-items: center;
`;
const CheckIconWrapper = styled(Check)`
  margin-left: 0.3rem;
  font-size: 0.8rem;
  color: green;
`;

const ClearIconWrapper = styled(Clear)`
  margin-left: 0.3rem;
  font-size: 0.8rem;
  color: red;
`;

const ButtonWrapper = styled(Box)`
  display: flex;
  justify-content: flex-end;
  margin-top: 3rem;
  height: 10%;
`;

function ResetPassword() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [tooltipIsOpen, setTooltipIsOpen] = useState(false);
  const [errorMessage, setΕrrorMessage] = useState("");
  const userId = searchParams.get("uuid");

  const { mutate } = useMutation(resetPassword, {
    onSuccess: (data) => {
      navigate("/auth/sign-in");
    },
    onError: (error) => {
      switch (error.response.status) {
        case 406: {
          setΕrrorMessage(t("User not found."));
          break;
        }
        default: {
          setΕrrorMessage(t("A network error occured. Please try again later"));
        }
      }
    },
  });
  const initialValues = {
    userPassword: "",
    retypePassword: "",
  };

  const EightChars = /.{8,}/;
  const UpperCaseCharacter = /(.*[A-Z].*)/;
  const LowerCaseCharacter = /(.*[a-z].*)/;
  const AtLeastOneDigit = /(.*\d.*)/;
  const SpecialCharacter = /(.*\W.*)/;
  const AllMatches =
    /^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9])(?=.*?[#?!@$%^&*-]).{8,}$/;

  const validationSchema = Yup.object({
    userPassword: Yup.string()
      .required(t("Please enter your new password."))
      .matches(AllMatches, t("Please type password based on requirements")),
    retypePassword: Yup.string()
      .required(t("Please retype your password."))
      .oneOf([Yup.ref("userPassword")], t("Your passwords do not match.")),
  });
  return (
    <BodyWrapper>
      {errorMessage && (
        <Alert sx={{ marginTop: 5, marginBottom: 5 }} severity="error">
          {errorMessage}
        </Alert>
      )}
      <TypographyWrapper>
        <Typography variant="h5">{t("PASSWORD RESET")}</Typography>
        <Typography variant="h6">
          {t("You asked to change your password")}
        </Typography>
      </TypographyWrapper>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={(values, { setSubmitting }) => {
          setSubmitting(false);
          mutate({ userId, password: values.userPassword });
        }}
      >
        {({ submitForm, errors, values, isSubmitting }) => (
          <StyledForm>
            <TextField
              label={
                <LabelWrapper>
                  <Typography variant="h6">
                    {t("type the new password")}
                  </Typography>
                  <HtmlTooltip
                    placement="top-start"
                    // open={tooltipIsOpen}
                    open={!!errors.userPassword || tooltipIsOpen}
                    onOpen={() => setTooltipIsOpen(true)}
                    onClose={() => setTooltipIsOpen(false)}
                    title={
                      <>
                        <Typography
                          sx={{ fontSize: "0.7rem", fontWeight: "600" }}
                        >
                          {t("Password should contain")}
                        </Typography>
                        <ListWrapper sx={{ listStyleType: "disc" }}>
                          <ListItem sx={{ display: "list-item" }}>
                            <ListItemsWrapper>
                              {t("At least 8 characters")}
                              {values?.userPassword?.match(EightChars) ? (
                                <CheckIconWrapper />
                              ) : (
                                <ClearIconWrapper />
                              )}
                            </ListItemsWrapper>
                          </ListItem>
                          <ListItem sx={{ display: "list-item" }}>
                            <ListItemsWrapper>
                              {t("At least one upper case letter")}
                              {values?.userPassword?.match(
                                UpperCaseCharacter
                              ) ? (
                                <CheckIconWrapper />
                              ) : (
                                <ClearIconWrapper />
                              )}
                            </ListItemsWrapper>
                          </ListItem>
                          <ListItem sx={{ display: "list-item" }}>
                            <ListItemsWrapper>
                              {t("At least one lower case letter")}
                              {values?.userPassword?.match(
                                LowerCaseCharacter
                              ) ? (
                                <CheckIconWrapper />
                              ) : (
                                <ClearIconWrapper />
                              )}
                            </ListItemsWrapper>
                          </ListItem>
                          <ListItem sx={{ display: "list-item" }}>
                            <ListItemsWrapper>
                              {t("At least one digit")}
                              {values?.userPassword?.match(AtLeastOneDigit) ? (
                                <CheckIconWrapper />
                              ) : (
                                <ClearIconWrapper />
                              )}
                            </ListItemsWrapper>
                          </ListItem>
                          <ListItem sx={{ display: "list-item" }}>
                            <ListItemsWrapper>
                              {t("At least one special character (Ex. !@#$%)")}
                              {values?.userPassword?.match(SpecialCharacter) ? (
                                <CheckIconWrapper />
                              ) : (
                                <ClearIconWrapper />
                              )}
                            </ListItemsWrapper>
                          </ListItem>
                        </ListWrapper>
                      </>
                    }
                  >
                    <InfoIcon sx={{ ml: 2, fontSize: "1.3rem" }} />
                  </HtmlTooltip>
                </LabelWrapper>
              }
              placeholder={t("type the new password")}
              fullWidth
              margin="normal"
              name="userPassword"
              type="password"
            />
            <TextField
              label={
                <Typography variant="h6">
                  {t("retype the new password")}
                </Typography>
              }
              placeholder={t("retype the new password")}
              fullWidth
              margin="normal"
              name="retypePassword"
              type="password"
            />
            <ButtonWrapper>
              <PrimaryButton
                onClick={submitForm}
                sx={{ width: "25%", fontSize: "1rem" }}
                disabled={isSubmitting ? true : false}
              >
                {isSubmitting ? (
                  <CircularProgress
                    sx={{ color: "#fff", marginRight: "0.5rem" }}
                    size={25}
                  />
                ) : null}
                {t("LOGIN")}
              </PrimaryButton>
            </ButtonWrapper>
          </StyledForm>
        )}
      </Formik>
    </BodyWrapper>
  );
}

export default ResetPassword;
